import { useState } from 'react';
import { Box, useMantineTheme, Paper, Group, Avatar, Stack, Text, Button, ActionIcon, Drawer, ScrollArea } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { Outlet, useLocation, Link, useNavigate } from 'react-router-dom';
import { IconUser, IconSun, IconMoon, IconMenu2, IconSearch } from '@tabler/icons-react';
import { LeftNav } from './LeftNav';
import { RightSidebar } from './RightSidebar';
import { MobileBottomNav } from './MobileBottomNav';
import { TopNavbar, NAVBAR_HEIGHT } from './TopNavbar';
import { Logo } from '../common/Logo';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

// Pages where the right sidebar is hidden
const HIDE_RIGHT_SIDEBAR: string[] = [];

// Pages where the unauthenticated CTA banner is hidden
const HIDE_CTA_BANNER = ['/login', '/register', '/forgot-password', '/reset-password'];

const GAP = 12; // px
const LEFT_NAV_WIDTH = 280;
const RIGHT_SIDEBAR_WIDTH = 340;

export function ThreeColumnLayout() {
  const theme = useMantineTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { activeColorScheme, toggleColorScheme } = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isDesktop = useMediaQuery(`(min-width: ${theme.breakpoints.lg})`);
  const isTablet = useMediaQuery(`(min-width: ${theme.breakpoints.md})`);
  const isMobile = !useMediaQuery(`(min-width: ${theme.breakpoints.sm})`);

  const hideRightSidebar = HIDE_RIGHT_SIDEBAR.includes(location.pathname);
  const showRightSidebar = isDesktop && !hideRightSidebar;
  const hideCTABanner = HIDE_CTA_BANNER.some(p => location.pathname.startsWith(p));
  const showLeftNav = isTablet;

  const minContentHeight = `calc(100vh - ${NAVBAR_HEIGHT + GAP * 2}px)`;

  const gridColumns = showLeftNav && showRightSidebar
    ? `${LEFT_NAV_WIDTH}px 1fr ${RIGHT_SIDEBAR_WIDTH}px`
    : showLeftNav
    ? `${LEFT_NAV_WIDTH}px 1fr`
    : showRightSidebar
    ? `1fr ${RIGHT_SIDEBAR_WIDTH}px`
    : '1fr';

  return (
    <Box
      style={{
        minHeight: '100vh',
        backgroundColor: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-8))',
      }}
    >
      {/* Full-width sticky navbar */}
      {!isMobile && <TopNavbar />}

      {/* Mobile header — logo + search + hamburger */}
      {isMobile && (
        <>
          <Box
            component="header"
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 200,
              backgroundColor: 'var(--mantine-color-body)',
              borderBottom: '1px solid var(--mantine-color-default-border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.625rem 1rem',
              gap: '0.5rem',
            }}
          >
            <Logo size="sm" showText />
            <Group gap="xs" wrap="nowrap">
              <ActionIcon
                onClick={() => navigate('/search')}
                size="md"
                radius="xl"
                variant="subtle"
                aria-label="Search"
              >
                <IconSearch size={20} />
              </ActionIcon>
              <ActionIcon
                onClick={() => toggleColorScheme()}
                size="md"
                radius="xl"
                variant="subtle"
                title={activeColorScheme === 'dark' ? 'Light mode' : 'Dark mode'}
              >
                {activeColorScheme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
              </ActionIcon>
              <ActionIcon
                onClick={() => setDrawerOpen(true)}
                size="md"
                radius="xl"
                variant="subtle"
                aria-label="Menu"
              >
                <IconMenu2 size={20} />
              </ActionIcon>
            </Group>
          </Box>

          {/* Mobile nav drawer */}
          <Drawer
            opened={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            size={280}
            padding={0}
            withCloseButton={false}
            styles={{ body: { padding: 0, height: '100%' } }}
          >
            <ScrollArea h="100%" p="xs" onClick={() => setDrawerOpen(false)}>
              <LeftNav />
            </ScrollArea>
          </Drawer>
        </>
      )}

      {/* Content container */}
      <Box
        style={{
          width: '100%',
          maxWidth: 1600,
          margin: '0 auto',
          padding: isMobile ? 0 : `${GAP}px`,
        }}
      >
        <Box
          style={{
            display: 'grid',
            gridTemplateColumns: gridColumns,
            gap: `${GAP}px`,
            alignItems: 'stretch',
          }}
        >
          {/* Left Nav — scrolls with the page, stretches to fill column */}
          {showLeftNav && (
            <Box
              component="nav"
              style={{
                backgroundColor: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-7))',
                borderRadius: '0.75rem',
                padding: '0.75rem 0.5rem',
                boxShadow: 'light-dark(0 1px 3px rgba(0,0,0,.05), 0 1px 3px rgba(0,0,0,.25))',
              }}
            >
              <LeftNav />
            </Box>
          )}

          {/* Main Content — sets the column height */}
          <Stack gap={GAP} style={{ minWidth: 0 }}>
            {!isAuthenticated && !hideCTABanner && (
              <Paper withBorder radius={isMobile ? 0 : 'lg'} p="md" style={{ borderColor: 'var(--mantine-color-brand-3)', borderLeft: 'none', borderRight: 'none' }}>
                <Group wrap="nowrap" gap="md" align="center">
                  {!isMobile && (
                    <Avatar size={42} radius="xl" color="brand">
                      <IconUser size={22} />
                    </Avatar>
                  )}
                  <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                    <Text fw={700} size="sm">Your reviews can help millions of shoppers.</Text>
                    <Text size="xs" c="dimmed">Join CartCritic — it's free and takes 30 seconds.</Text>
                  </Stack>
                  <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                    <Button component={Link} to="/register" size="xs" radius="xl">Sign up</Button>
                    <Button component={Link} to="/login" size="xs" radius="xl" variant="subtle">Log in</Button>
                  </Group>
                </Group>
              </Paper>
            )}
            <Box
              component="main"
              style={{
                minHeight: isMobile ? '100vh' : minContentHeight,
                backgroundColor: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-7))',
                borderRadius: isMobile ? 0 : '0.75rem',
                boxShadow: isMobile ? 'none' : 'light-dark(0 1px 3px rgba(0,0,0,.05), 0 1px 3px rgba(0,0,0,.25))',
                paddingBottom: isMobile ? 90 : 0,
                overflowX: 'hidden',
              }}
            >
              <Outlet />
            </Box>
          </Stack>

          {/* Right Sidebar — scrolls with the page */}
          {showRightSidebar && (
            <Box
              component="aside"
              style={{
                alignSelf: 'start',
                backgroundColor: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-7))',
                borderRadius: '0.75rem',
                padding: '1rem',
                boxShadow: 'light-dark(0 1px 3px rgba(0,0,0,.05), 0 1px 3px rgba(0,0,0,.25))',
              }}
            >
              <RightSidebar />
            </Box>
          )}
        </Box>
      </Box>

      {/* Mobile Bottom Navigation */}
      {isMobile && <MobileBottomNav />}
    </Box>
  );
}
